import { Component, ReactNode } from "react";
import RouteFallback from "./RouteFallback";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches React.lazy() ChunkLoadError (typically a stale build after deploy)
 * and auto-retries by hard-reloading after a short delay so the user never
 * lands on a blank screen.
 */
export default class LazyChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };
  private timer: number | null = null;

  static getDerivedStateFromError(error: unknown): State {
    const name = (error as Error)?.name ?? "";
    const msg = (error as Error)?.message ?? "";
    const isChunk =
      name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /dynamically imported module/i.test(msg);
    return { hasError: isChunk };
  }

  componentDidCatch() {
    if (this.timer != null) return;
    this.timer = window.setTimeout(() => {
      window.location.reload();
    }, 1500);
  }

  componentWillUnmount() {
    if (this.timer != null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  render() {
    if (this.state.hasError) {
      return <RouteFallback variant="default" />;
    }
    return this.props.children;
  }
}